import { useState, useMemo, useCallback } from "react";
import React from "react";
import { DateTime } from "luxon";
import { cn } from "../lib/utils";
import { formatCurrency } from "../utils/format";
import { useExchangeRates } from "../lib/supabase/exchange-rates/supabaseExchangeRateService";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "./ui/tooltip";
import { Input } from "./ui/input";
import { Search } from "lucide-react";

interface Column<T> {
  key: keyof T;
  header: string;
  type?: 'text' | 'currency' | 'date' | 'custom';
  width?: string;
  render?: (value: any, item?: T) => React.ReactNode;
  editable?: boolean;
}

interface GroupConfig<T> {
  key: keyof T;
  render?: (value: any) => string;
}

interface SheetTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onDelete: (index: number) => void;
  showTotals?: boolean;
  groupBy?: GroupConfig<T>;
  searchable?: boolean;
}

export function SheetTable<T extends { id: string; metadata?: { notes?: string; currencyType?: string } }>({
  data,
  columns,
  showTotals = false,
  groupBy,
  searchable = true
}: SheetTableProps<T>) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const { data: exchangeRates = [] } = useExchangeRates();

  // Get exchange rate for current month
  const currentExchangeRate = useMemo(() => {
    if (!data.length) return null;
    const firstItem = data[0];
    const date = 'date' in firstItem ? (firstItem as any).date : null;
    if (!date) return null;

    const month = date.month - 1; // Convert to 0-based month
    const year = date.year;

    return exchangeRates.find(rate => {
      const rateDate = rate.date;
      return rateDate.month === month && rateDate.year === year;
    })?.rate ?? null;
  }, [data, exchangeRates]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;

    const searchLower = searchTerm.toLowerCase().trim();
    
    return data.filter(item => {
      // Search through all columns
      return columns.some(column => {
        const value = item[column.key];
        
        if (value == null) return false;
        
        // Handle different column types
        if (column.type === 'currency') {
          // Search both numeric value and formatted currency
          const numericMatch = String(value).toLowerCase().includes(searchLower);
          const metadata = 'metadata' in item ? (item.metadata as any) : null;
          const isUSD = metadata?.currencyType === 'usd';
          const formattedValue = formatCurrency(value as number, isUSD ? '$' : '£').toLowerCase();
          return numericMatch || formattedValue.includes(searchLower);
        } else if (column.type === 'date') {
          // Search formatted date
          if (value && typeof value === 'object' && 'toFormat' in value) {
            const formattedDate = (value as unknown as DateTime).toFormat('DDD').toLowerCase();
            return formattedDate.includes(searchLower);
          }
          return false;
        } else if (column.render) {
          // For custom renders, try to get the rendered string
          const rendered = column.render(value, item);
          if (typeof rendered === 'string') {
            return rendered.toLowerCase().includes(searchLower);
          }
          return false;
        } else {
          // Default text search
          return String(value).toLowerCase().includes(searchLower);
        }
      });
    });
  }, [data, searchTerm, columns, currentExchangeRate]);

  const groupedData = useMemo(() => {
    if (!groupBy) return { ungrouped: filteredData };

    return filteredData.reduce((acc, item) => {
      const groupValue = item[groupBy.key];
      const groupKey = groupBy.render ? groupBy.render(groupValue) : String(groupValue || 'Other');

      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    }, {} as Record<string, T[]>);
  }, [filteredData, groupBy]);

  const calculateColumnTotal = (column: Column<T>, items: T[] = data) => {
    const totals = { gbp: 0, usd: 0 };

    if (column.type !== 'currency') return null;

    items.forEach(item => {
      const value = item[column.key];
      if (typeof value === 'number') {
        const metadata = 'metadata' in item ? (item.metadata as any) : null;
        const isUSD = metadata?.currencyType === 'usd';
        if (isUSD) {
          totals.usd += value;
        } else {
          totals.gbp += value;
        }
      }
    });

    return totals;
  };

  const columnTotals = useMemo(() => {
    if (!showTotals) return null;
    return columns.reduce((acc, column) => {
      acc[column.key as string] = calculateColumnTotal(column, filteredData);
      return acc;
    }, {} as Record<string, { gbp: number; usd: number } | null>);
  }, [filteredData, columns, showTotals]);

  const renderCurrencyValue = (value: number, isUSD: boolean) => {
    if (isUSD && currentExchangeRate) {
      const convertedValue = value * currentExchangeRate;
      return (
        <span className="text-left text-brand-green font-medium">
          {formatCurrency(convertedValue, '£')}
          <span className="text-xs text-muted-foreground ml-1">(USD {formatCurrency(value, '$')})</span>
        </span>
      );
    }
    if (isUSD && !currentExchangeRate) {
      return (
        <div className="flex items-center gap-1">
          <span className="text-left text-brand-gold font-medium">
            {formatCurrency(value, '$')}
          </span>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="text-brand-gold text-xs">*</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>USD amount not converted - exchange rate unavailable</p>
            </TooltipContent>
          </Tooltip>
        </div>
      );
    }
    return (
      <span className="text-left text-brand-green font-medium">
        {formatCurrency(value, isUSD ? '$' : '£')}
      </span>
    );
  };

  const renderCurrencyCell = (gbp: number, usd: number) => {
    const convertedUsd = currentExchangeRate ? usd * currentExchangeRate : usd;
    const total = gbp + convertedUsd;
    const hasUSD = usd !== 0;
    const hasNoExchangeRate = !currentExchangeRate && hasUSD;

    if (gbp === 0 && usd === 0) return null;

    return (
      <div className="flex flex-col items-start gap-0.5">
        {gbp !== 0 && usd !== 0 ? (
          <>
            <div className="flex flex-row items-center gap-1">
              <span className="text-left text-brand-green font-medium">
                {formatCurrency(gbp, '£')}
              </span>
              <span className="text-left text-muted-foreground font-medium text-sm">
                (+{formatCurrency(usd, '$')})
              </span>
            </div>
            <span className={cn(
              "text-left font-medium",
              hasNoExchangeRate ? "text-brand-gold" : "text-brand-green"
            )}>
              {currentExchangeRate ? formatCurrency(total, '£') : `${formatCurrency(gbp, '£')} + ${formatCurrency(usd, '$')}`}
              {hasNoExchangeRate && (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <span className="text-brand-gold text-xs ml-1">*</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>USD amount not converted - exchange rate unavailable</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </span>
          </>
        ) : gbp !== 0 ? (
          <span className="text-left text-brand-green font-medium">
            {formatCurrency(gbp, '£')}
          </span>
        ) : currentExchangeRate ? (
          <>
            <span className="text-left text-brand-green font-medium">
              {formatCurrency(usd * currentExchangeRate, '£')}
            </span>
            <span className="text-xs text-muted-foreground ml-1">(USD {formatCurrency(usd, '$')})</span>
          </>
        ) : (
          <div className="flex items-center gap-1">
            <span className="text-left text-brand-gold font-medium">
              {formatCurrency(usd, '$')}
            </span>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="text-brand-gold text-xs">*</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>USD amount not converted - exchange rate unavailable</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  const renderCell = (item: T, column: Column<T>) => {
    const value = item[column.key];

    if (column.type === 'custom') {
      return (
        <div className="h-full w-full px-4 py-3">
          {column.render ? column.render(value, item) : null}
        </div>
      );
    }

    return (
      <div
        className={cn(
          "h-full w-full px-4 py-3 transition-colors duration-100",
        )}
      >
        {column.render ? column.render(value, item) :
          column.type === 'currency'
            ? (() => {
              const metadata = 'metadata' in item ? (item.metadata as any) : null;
              const isUSD = metadata?.currencyType === 'usd';
              return renderCurrencyValue(value as number, isUSD);
            })()
            : column.type === 'date'
              ? (value as DateTime).toFormat('DDD')
              : value as string}
      </div>
    );
  };

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  // Create a cache key for row memoization
  const createRowCacheKey = useCallback((item: T) => {
    return `${item.id}-${JSON.stringify(columns.map(c => c.key))}-${currentExchangeRate}`;
  }, [columns, currentExchangeRate]);

  // Memoized row cache - individual rows are cached to avoid recreation during search
  const rowCache = useMemo(() => {
    const cache = new Map<string, React.ReactElement>();
    
    data.forEach(item => {
      const cacheKey = createRowCacheKey(item);
      
      if (!cache.has(cacheKey)) {
        cache.set(cacheKey, (
          <tr
            key={item.id}
            className={cn(
              "border-b border-brand-border dark:border-brand-border transition-colors group relative",
              "hover:bg-brand-light data-[state=selected]:bg-brand-light",
            )}
          >
            {columns.map((column) => (
              <td
                key={column.key as string}
                className="p-0"
              >
                {renderCell(item, column)}
                {item.metadata?.notes && column === columns[columns.length - 1] && (
                  <Tooltip delayDuration={0} key={item.id}>
                    <TooltipTrigger asChild>
                      <div className="absolute top-0 right-0 w-0 h-0 border-t-[8px] border-t-white border-l-[8px] border-l-transparent z-10" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[300px] break-words">
                      <p>{item.metadata.notes}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </td>
            ))}
          </tr>
        ));
      }
    });
    
    return cache;
  }, [data, columns, currentExchangeRate, createRowCacheKey]);

  // Get memoized rows for current filtered data
  const getMemoizedRows = useCallback(() => {
    return Object.entries(groupedData).map(([groupKey, groupItems]) => (
      <React.Fragment key={groupKey}>
        {groupBy && (
          <tr className="border-b border-t border-brand-border dark:border-brand-border bg-brand-light">
            <td
              colSpan={columns.length}
              className="px-4 py-1 cursor-pointer hover:bg-brand-dark"
              onClick={() => toggleGroup(groupKey)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{groupKey}</span>
                  <span className="text-sm text-muted-foreground">({groupItems.length})</span>
                </div>
                {showTotals && (
                  <div className="flex items-center gap-4 text-sm">
                    {columns.filter(col => col.type === 'currency').map((column) => {
                      const sectionTotals = calculateColumnTotal(column, groupItems);
                      return (
                        <div key={column.key as string} className="flex items-center gap-1">
                          <span className="text-muted-foreground">{column.header}:</span>
                          <span className="font-medium">
                            {renderCurrencyCell(
                              sectionTotals?.gbp ?? 0,
                              sectionTotals?.usd ?? 0
                            ) || '£0.00'}
                          </span>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-1 ml-4">
                      <span className="text-muted-foreground">Total:</span>
                      <span className="font-bold">
                        {(() => {
                          const currencyColumns = columns.filter(col => col.type === 'currency');
                          const totalGbp = currencyColumns.reduce((sum, col) => {
                            const totals = calculateColumnTotal(col, groupItems);
                            return sum + (totals?.gbp ?? 0);
                          }, 0);
                          const totalUsd = currencyColumns.reduce((sum, col) => {
                            const totals = calculateColumnTotal(col, groupItems);
                            return sum + (totals?.usd ?? 0);
                          }, 0);
                          return renderCurrencyCell(totalGbp, totalUsd);
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}
        {!collapsedGroups.has(groupKey) && groupItems.map((item) => {
          const cacheKey = createRowCacheKey(item);
          return rowCache.get(cacheKey) || null;
        })}
      </React.Fragment>
    ));
  }, [groupedData, groupBy, columns, collapsedGroups, rowCache, createRowCacheKey, toggleGroup]);

  // Check if there are any USD amounts without exchange rate
  const hasUSDWithoutExchangeRate = useMemo(() => {
    if (currentExchangeRate) return false;
    return filteredData.some(item => {
      const metadata = 'metadata' in item ? (item.metadata as any) : null;
      return metadata?.currencyType === 'usd';
    });
  }, [filteredData, currentExchangeRate]);

  return (
    <div className="w-full rounded-lg border border-brand-border bg-brand-background text-foreground">
      {hasUSDWithoutExchangeRate && (
        <div className="p-3 bg-brand-gold/10 border-b border-brand-gold/20 rounded-t-lg">
          <div className="flex items-center gap-2 text-brand-gold">
            <span className="text-sm font-medium">Exchange rate unavailable</span>
            <span className="text-sm">- USD amounts shown are not converted to GBP</span>
          </div>
        </div>
      )}
      {searchable && (
        <div className="p-4 border-b border-brand-border dark:border-brand-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-brand-background border-brand-border text-foreground placeholder:text-muted-foreground"
            />
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-brand-border dark:border-brand-border transition-colors bg-brand-dark/60 dark:bg-brand-dark/60 rounded-t-lg">
              {columns.map((column, index) => (
                <th
                  key={column.key as string}
                  className={cn(
                    "h-10 px-4 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0",
                    index === 0 && "rounded-tl-lg",
                    index === columns.length - 1 && "rounded-tr-lg"
                  )}
                  style={{ width: column.width }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {getMemoizedRows()}
            {showTotals && columnTotals && (
              <tr className="border-t border-brand-border transition-colors bg-brand-light rounded-b-lg">
                {columns.map((column, index) => (
                  <td key={column.key as string} className={cn("p-0", index === 0 && "rounded-bl-lg", index === columns.length - 1 && "rounded-br-lg")}>
                    {index === 0 ? (
                      <div className="h-full w-full px-4 py-2">
                        <span className="block truncate text-left font-medium">
                          Total
                        </span>
                      </div>
                    ) : column.type === 'currency' ? (
                      <div className="h-full w-full px-4 py-2">
                        {renderCurrencyCell(
                          columnTotals[column.key as string]?.gbp ?? 0,
                          columnTotals[column.key as string]?.usd ?? 0
                        )}
                      </div>
                    ) : column.type === 'custom' ? (
                      <div className="h-full w-full px-4 py-2">
                        <div className="flex justify-end">
                          {renderCurrencyCell(
                            Object.values(columnTotals)
                              .filter(Boolean)
                              .reduce((sum, total) => sum + (total?.gbp ?? 0), 0),
                            Object.values(columnTotals)
                              .filter(Boolean)
                              .reduce((sum, total) => sum + (total?.usd ?? 0), 0)
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="h-full w-full px-4 py-2">
                        <span className="block truncate"></span>
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
} 